// src/ConsumerApp/Screens/Home/components/FrequentlyBoughtSection.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CaretRight from '../../../../assets/icons/CaretRight.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';

const IMAGE_BASE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

function RecentItemImage({ imageUri }) {
  const [hasError, setHasError] = useState(false);

  if (!imageUri || hasError) {
    return (
      <div style={styles.recentFallback}>
        <img src={ToteIcon} alt="fallback" style={{ width: 22, height: 22, opacity: 0.35 }} />
      </div>
    );
  }

  return (
    <img
      src={imageUri}
      alt="Product"
      style={styles.recentImg}
      onError={() => setHasError(true)}
      loading="lazy"
    />
  );
}

export default function FrequentlyBoughtSection({ items = [], isLoading = false }) {
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;

    const hasOverflow = maxScroll > 5;
    const isAtStart = scrollLeft <= 5;
    const isAtEnd = scrollLeft >= maxScroll - 5;

    setCanScrollLeft(!isAtStart && hasOverflow);
    setCanScrollRight(!isAtEnd && hasOverflow);
  }, []);

  useEffect(() => {
    checkScrollState();

    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', checkScrollState, { passive: true });
    window.addEventListener('resize', checkScrollState);

    const observer = new ResizeObserver(checkScrollState);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', checkScrollState);
      window.removeEventListener('resize', checkScrollState);
      observer.disconnect();
    };
  }, [checkScrollState, items]);

  const handleScroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollAmount = Math.max(el.clientWidth * 0.75, 240);
    const targetScroll =
      direction === 'left' ? el.scrollLeft - scrollAmount : el.scrollLeft + scrollAmount;

    el.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  };

  if (isLoading || !items || items.length === 0) return null;

  return (
    <section style={{ width: '100%', position: 'relative' }}>
      <style>{`
        .bb-frequently-bought-shell {
          position: relative;
          width: 100%;
          display: flex;
          align-items: center;
        }

        .bb-frequently-bought-scroll {
          display: flex;
          overflow-x: auto;
          overflow-y: hidden;
          gap: 16px;
          width: 100%;
          padding: 4px 2px 14px 2px;
          scroll-behavior: smooth;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          scroll-snap-type: x mandatory;
          box-sizing: border-box;
        }

        .bb-frequently-bought-scroll::-webkit-scrollbar {
          display: none;
        }

        .bb-frequently-bought-card {
          width: 164px;
          min-width: 164px;
          background-color: #FFFFFF;
          border-radius: 12px;
          border: 0.5px solid #CBD5E1;
          overflow: hidden;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          scroll-snap-align: start;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          box-sizing: border-box;
        }

        .bb-frequently-bought-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 14px rgba(0, 0, 0, 0.06);
        }

        /* ── Floating Nav Buttons ── */
        .bb-freq-nav-btn {
          position: absolute;
          top: calc(50% - 10px);
          transform: translateY(-50%);
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background-color: #FFFFFF;
          border: 1px solid #CBD5E1;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.14);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
          padding: 0;
        }

        .bb-freq-nav-btn:hover {
          background-color: #F8FAFC;
          box-shadow: 0 6px 16px rgba(15, 23, 42, 0.22);
          transform: translateY(-50%) scale(1.05);
        }

        .bb-freq-nav-btn:active {
          transform: translateY(-50%) scale(0.95);
        }

        .bb-freq-nav-left {
          left: -14px;
        }

        .bb-freq-nav-right {
          right: -14px;
        }

        @media (max-width: 768px) {
          .bb-freq-nav-btn {
            display: none !important;
          }
          .bb-frequently-bought-card {
            width: 142px;
            min-width: 142px;
          }
        }
      `}</style>

      <div style={styles.sectionHeader}>
        <h3 style={styles.sectionTitle}>Frequently bought by buyers</h3>
      </div>

      <div className="bb-frequently-bought-shell">
        {/* Left Scroll Navigation */}
        {canScrollLeft && (
          <button
            type="button"
            className="bb-freq-nav-btn bb-freq-nav-left"
            onClick={() => handleScroll('left')}
            aria-label="Scroll backward"
          >
            <img
              src={CaretRight}
              alt="‹"
              style={{ width: 14, height: 14, transform: 'rotate(180deg)' }}
            />
          </button>
        )}

        {/* Scroll Container */}
        <div ref={scrollRef} className="bb-frequently-bought-scroll">
          {items.map((item) => {
            const productId = item.product_id || item._id;
            const name = item.product_name || item.name || 'Unknown Product';
            const imgPath = item.product_image || item.product_image_url || item.image;
            const buyNowPrice =
              item?.buy_now?.unit_price ||
              item?.selling_price ||
              item?.unit_price ||
              item?.price;
            const mrp = item?.mrp;

            return (
              <div
                key={productId}
                className="bb-frequently-bought-card"
                onClick={() =>
                  navigate('/consumer/productdetails', { state: { productId } })
                }
              >
                <RecentItemImage
                  imageUri={imgPath ? `${IMAGE_BASE_URL}${imgPath}` : null}
                />

                <div style={styles.productBody}>
                  <p style={styles.productTitle} title={name}>
                    {name}
                  </p>

                  <div style={styles.productPriceRow}>
                    <span style={styles.sellingPrice}>
                      ₹{Number(buyNowPrice || 0).toLocaleString('en-IN')}
                    </span>
                    {mrp && Number(mrp) > Number(buyNowPrice) && (
                      <span style={styles.mrpPrice}>
                        ₹{Number(mrp).toLocaleString('en-IN')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right Scroll Navigation */}
        {canScrollRight && (
          <button
            type="button"
            className="bb-freq-nav-btn bb-freq-nav-right"
            onClick={() => handleScroll('right')}
            aria-label="Scroll forward"
          >
            <img src={CaretRight} alt="›" style={{ width: 14, height: 14 }} />
          </button>
        )}
      </div>
    </section>
  );
}

const styles = {
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 'clamp(15px, 2vw, 18px)',
    fontWeight: '800',
    color: '#2B2A29',
    margin: 0,
    letterSpacing: '-0.2px',
  },
  recentImg: {
    width: '100%',
    height: 94,
    objectFit: 'contain',
    marginTop: 8,
    display: 'block',
  },
  recentFallback: {
    width: '100%',
    height: 94,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
  },
  productBody: {
    padding: '8px 12px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  productTitle: {
    fontSize: 12.5,
    lineHeight: '16px',
    color: '#2B2A29',
    fontWeight: '600',
    height: 32,
    margin: 0,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  productPriceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    marginTop: 2,
  },
  sellingPrice: {
    fontSize: 13.5,
    color: '#2B2A29',
    fontWeight: '800',
  },
  mrpPrice: {
    fontSize: 11,
    color: '#94A3B8',
    textDecoration: 'line-through',
  },
};